/**
 * i18n keys for the procurement contract-review feature.
 *
 * Wired into `react-i18next` as the `contractReview` namespace. Components
 * consume it via `const { t } = useTranslation('contractReview')`.
 */

export const contractReviewPtBr: Record<string, unknown> = {
  page: {
    listTitle: 'Revisões de contrato',
    listSubtitle: 'Acompanhe contratos em revisão pela equipe e pela IA.',
    detailBackToList: 'Lista',
    intakeTitle: 'Enviar contrato para revisão',
    intakeSubtitle:
      'O documento passa por uma análise inicial automática e nossa equipe entra em contato.',
    intakeBranding: 'Recebendo solicitação para',
    trackTitle: 'Acompanhamento da revisão de contrato',
    settingsTitle: 'Configurações de revisão de contrato',
    newRequestTitle: 'Nova revisão de contrato',
  },
  common: {
    loading: 'Carregando...',
    loadingRequest: 'Carregando solicitação...',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    save: 'Salvar',
    copy: 'Copiar',
    submit: 'Enviar',
    download: 'Baixar',
    open: 'Abrir',
    close: 'Fechar',
    optional: 'opcional',
    required: 'obrigatório',
    yes: 'Sim',
    no: 'Não',
    notProvided: 'Não informado',
    none: 'Nenhum(a)',
    by: 'Por',
    at: 'Em',
  },
  status: {
    internal: {
      DRAFT: 'Rascunho',
      PENDING_INTAKE_ACK: 'Aguardando triagem',
      AI_PROCESSING: 'IA analisando',
      READY_FOR_REVIEW: 'Pronto para revisão humana',
      IN_REVIEWER_TRIAGE: 'Em triagem pelo revisor',
      AWAITING_VENDOR: 'Aguardando fornecedor',
      AWAITING_SIGNING: 'Pronto para assinatura',
      COMPLETED: 'Concluído (assinado)',
      CANCELLED: 'Cancelado',
      FAILED: 'Falhou',
    },
    external: {
      DRAFT: 'Rascunho',
      PENDING_INTAKE_ACK: 'Em revisão pela equipe',
      AI_PROCESSING: 'Em revisão pela equipe',
      READY_FOR_REVIEW: 'Em revisão pela equipe',
      IN_REVIEWER_TRIAGE: 'Em revisão pela equipe',
      AWAITING_VENDOR: 'Ação necessária — envie uma nova versão',
      AWAITING_SIGNING: 'Aprovado — preparando assinatura',
      COMPLETED: 'Concluído — assinado',
      CANCELLED: 'Solicitação cancelada',
      FAILED: 'Erro técnico — falaremos com você',
    },
  },
  severity: {
    LOW: 'Baixa',
    MEDIUM: 'Média',
    HIGH: 'Alta',
    CRITICAL: 'Crítica',
  },
  decision: {
    PENDING: 'Pendente',
    ACCEPTED: 'Aceita',
    REJECTED: 'Rejeitada',
    RESOLVED: 'Resolvida',
  },
  category: {
    LEGAL: 'Legal',
    BUSINESS: 'Negócio',
    COMPLIANCE: 'Compliance',
    VENDOR_VALIDATION: 'Validação de fornecedor',
    OTHER: 'Outro',
  },
  source: {
    AGENT_LEGAL: 'Agente Legal',
    AGENT_BUSINESS_RISK: 'Agente Risco',
    AGENT_VENDOR_VALIDATION: 'Agente Validação',
    REVIEWER: 'Revisor',
  },
  versionSource: {
    INITIAL_INTAKE: 'Versão inicial',
    EXTERNAL_RESUBMISSION: 'Reenvio do fornecedor',
    INTERNAL_REVIEW: 'Versão interna',
    AI_GENERATED: 'Revisado (ajustes solicitados)',
  },
  tokenScope: {
    STATUS_VIEW: {
      label: 'Consulta de status',
      description: 'Permite abrir o portal externo e acompanhar o status da revisão.',
    },
    DOWNLOAD_VERSION: {
      label: 'Baixar documentos',
      description: 'Permite abrir o portal externo e gerar links de download das versões.',
    },
    UPLOAD_VERSION: {
      label: 'Enviar nova versão',
      description: 'Permite abrir o portal externo, comentar flags e enviar uma revisão em PDF.',
    },
  },
  redline: {
    title: 'Revisar alterações (redline)',
    hint: 'Revise e ajuste cada alteração antes de gerar a versão com controle de alterações do Word.',
    loading: 'Planejando as alterações com a IA…',
    empty: 'Nenhuma alteração pôde ser ancorada no documento. Você ainda pode enviar sem redline.',
    anchor: 'Trecho original',
    newText: 'Nova redação',
    comment: 'Comentário para a contraparte',
    generate: 'Gerar redline e enviar',
    skipped: '{{count}} alteração(ões) não puderam ser aplicadas e foram ignoradas.',
    changeType: {
      replace: 'Substituir',
      insert_after: 'Inserir depois',
      delete: 'Remover',
    },
  },
  buttons: {
    engageReview: 'Engajar revisão',
    engageReviewWaiting: 'Aguardando análise inicial…',
    forceAiRerun: 'Forçar reexecução',
    sendBack: 'Enviar para fornecedor revisar',
    sendBackWithCount: 'Enviar para fornecedor revisar ({{count}})',
    sendingBack: 'Enviando…',
    previewSendBack: 'Pré-visualizar PDF',
    previewSendBackLoading: 'Gerando prévia…',
    previewDraftDocx: 'Pré-visualizar rascunho',
    previewDraftDocxLoading: 'Gerando rascunho…',
    editDocument: 'Editar documento',
    exitEditing: 'Concluir edição',
    takeOverEditing: 'Assumir edição',
    reviewRedline: 'Revisar redline',
    approveForSigning: 'Aprovar e enviar para assinatura',
    approving: 'Aprovando…',
    markSigned: 'Marcar assinado',
    retryAi: 'Reagendar IA',
    resetToTriage: 'Reabrir revisão',
    addReviewerFlag: '+ Adicionar flag',
    bulkResolve: 'Resolver todas',
    bulkReject: 'Rejeitar todas',
    decideAccept: 'Aceitar',
    decideAcceptWithNote: 'Aceitar com comentário…',
    decideAcceptTooltip: 'Aceitar a flag e enviar ao fornecedor para correção.',
    decideReject: 'Rejeitar',
    decideRejectWithNote: 'Rejeitar com comentário…',
    decideRejectTooltip: 'Marcar como falso positivo. O fornecedor não verá esta flag.',
    decideResolve: 'Resolver',
    decideResolveWithNote: 'Resolver com comentário…',
    decideResolveTooltip: 'Já foi tratado fora do sistema. Fechar sem enviar ao fornecedor.',
    promoteGlobalFlag: 'Promover para global',
    setSigner: 'Definir signatário',
    requestRiskApproval: 'Solicitar aprovação de risco',
    approveRisk: 'Aprovar risco',
    rejectRisk: 'Rejeitar risco',
    runAiNow: 'Rodar IA agora',
    runningAi: 'Rodando…',
    engaging: 'Engajando…',
    create: 'Criar',
    creating: 'Criando…',
    edit: 'Editar',
    delete: 'Excluir',
    issueToken: 'Emitir acesso',
    revokeToken: 'Revogar',
    copyToken: 'Copiar token',
  },
  approvalStages: {
    LEGAL: 'Jurídico',
    BUSINESS_RISK: 'Gestor / risco',
    EXECUTIVE: 'Alçada superior',
  },
  tabs: {
    flags: 'Flags ({{count}})',
    pendingFlags: 'Flags pendentes ({{count}})',
    versions: 'Versões ({{count}})',
    tokens: 'Acesso externo',
    audit: 'Auditoria',
  },
  banner: {
    triageDone: 'Triagem concluída ({{total}} flag(s) — {{accepted}} aceitas).',
    triageDoneSendBack: 'Envie ao fornecedor para que ele revise os pontos aceitos.',
    triageDoneApprove: 'Nada para corrigir — aprove e envie para assinatura.',
    processingPreIntakeTitle: 'Análise inicial em andamento',
    processingPreIntakeBody:
      'Estamos extraindo CNPJ, contraparte e tipo de documento. A página atualiza sozinha quando termina.',
    processingFullReviewTitle: 'Revisão completa pela IA em andamento',
    processingFullReviewBody:
      'Os agentes Legal, Risco e Validação rodam em paralelo quando habilitados. A página atualiza sozinha quando as flags estiverem prontas.',
    readyToEngageTitle: 'Análise inicial concluída',
    readyToEngageBody:
      'Revise o resumo e os campos extraídos abaixo. Quando estiver pronto, clique em Engajar para iniciar a revisão completa pela IA.',
    someoneEditing: '{{name}} está editando este documento.',
    someoneEditingReadOnly: 'Visualização somente leitura enquanto {{name}} edita.',
    someoneEditingStale: 'A sessão de edição de {{name}} expirou. Você pode assumir a edição.',
  },
  editor: {
    title: 'Edição do documento',
    hint: 'Selecione um trecho do texto para substituir, inserir depois ou remover. Cada edição vira uma alteração rastreada e uma flag.',
    readOnly: 'Somente leitura',
    readOnlyHint: 'Aguarde o outro revisor liberar o documento para editar.',
    selectionPlaceholder: 'Selecione um trecho no texto para editar.',
    selectedLabel: 'Trecho selecionado',
    actionReplace: 'Substituir',
    actionInsertAfter: 'Inserir depois',
    actionDelete: 'Remover',
    newTextLabel: 'Nova redação',
    newTextPlaceholder: 'Digite a nova redação…',
    commentLabel: 'Comentário para a contraparte (opcional)',
    apply: 'Aplicar edição',
    cancel: 'Cancelar',
    editsCount_one: '{{count}} edição',
    editsCount_other: '{{count}} edições',
    removeEdit: 'Remover edição',
    saving: 'Salvando…',
    saved: 'Rascunho salvo',
    empty: 'Nenhuma edição ainda.',
  },
  detail: {
    aiSummaryTitle: 'Resumo da IA',
    aiSummaryInitialTitle: 'Resumo IA — Triagem inicial',
    collapseSummary: 'Recolher',
    expandSummary: 'Expandir',
    aiSummaryEmpty: 'A IA ainda não processou este documento.',
    extractedCnpj: 'CNPJ identificado',
    extractedCounterpartyName: 'Contraparte identificada',
    documentTypeGuess: 'Tipo do documento',
    primaryReviewer: 'Revisor responsável',
    primaryReviewerNone: 'Sem responsável definido',
    counterparty: 'Contraparte',
    contactEmail: 'E-mail de contato',
    nothingToPreview: 'Nenhum documento disponível para preview.',
    previewLoading: 'Carregando documento...',
    previewUnavailable: 'Preview indisponível para este tipo de arquivo ({{mime}}).',
    openInNewTab: 'Abrir o arquivo em nova aba',
    previewFormatted: 'Formatado',
    previewPlainText: 'Texto',
    showDocument: 'Mostrar documento',
    noActiveVersion: 'Nenhuma versão ativa',
    documentTypeBadge: 'Tipo: {{type}}',
    counterpartyBadge: 'Contraparte: {{name}}',
    cnpjBadge: 'CNPJ: {{cnpj}}',
    previewWindowTitle: 'Prévia do PDF de revisão',
    previewWindowLoading: 'Gerando a prévia do PDF…',
    versionDeprecated: 'versão anterior',
    versionActive: 'ativa',
    versionAiAnnotated: 'Revisado (ajustes solicitados)',
    versionEmpty: 'Nenhuma versão.',
    flagsEmpty: 'Nenhuma flag ainda.',
    flagsEmptyHint: 'Rode a análise da IA para gerar findings.',
    pendingFlagsEmpty: 'Nenhuma flag pendente.',
    pendingFlagsEmptyHint:
      'Quando você aceitar, resolver ou rejeitar uma flag, ela sai desta lista.',
    pendingCount: '{{count}} pendente(s)',
    reviewerNote: 'Nota do revisor: {{note}}',
    replyPlaceholder: 'Adicionar comentário nesta flag',
    replySend: 'Enviar comentário',
    replyAttach: 'Anexar arquivo',
    replyAttachmentRemove: 'Remover anexo',
    replyAttachmentTooMany: 'Máximo de {{count}} anexos por comentário',
    replyAttachmentTooLarge: '"{{name}}" excede o tamanho máximo de {{mb}}MB',
    replyAttachmentTypeNotAllowed: '"{{name}}" não é um tipo permitido (PDF, PNG, JPEG ou DOCX)',
    replyAttachmentDownloadFailed: 'Não foi possível abrir o anexo',
    externalCounterparty: 'Contraparte',
    internalReviewer: 'Time interno',
    internalReviewerFallback: 'Revisor interno',
    proposedWordingTitle: 'Redação sugerida',
    flagTitleLabel: 'Título',
    internalRationaleLabel: 'Racional interno (só revisores)',
    vendorMessageLabel: 'Mensagem para o fornecedor',
    vendorMessageHint: 'Enviada no PDF e no portal externo quando a flag for aceita.',
    legacyVendorFallbackWarning:
      'Esta flag ainda não tem mensagem para o fornecedor. O texto interno acima será enviado ao fornecedor no PDF/portal até você salvar uma mensagem dedicada.',
    flagSave: 'Salvar alterações',
    flagSaveDirty: 'Salvar',
    signerMissing: 'Defina o signatário antes de finalizar',
    riskApprovalRequired: 'A aprovação de risco é obrigatória antes de finalizar',
    approvalStagesRequired: 'Há aprovações obrigatórias pendentes antes de finalizar',
    legacyRiskPending: 'Aprovação de risco pendente (fluxo legado)',
    awaitingOtherApprover: 'Aguardando aprovação de outro gestor',
    auditEmpty: 'Nenhum evento de auditoria.',
    tokenIssued: 'Token mostrado uma única vez',
    tokenCopied: 'Token copiado.',
    tokenHelp:
      'Informe o e-mail que receberá o acesso. O token aparece uma única vez e deve ser entregue ao fornecedor pelo canal combinado.',
    tokenRecipientEmail: 'E-mail do destinatário',
    tokenRecipientEmailPlaceholder: 'legal@fornecedor.com',
    tokenIssuedAt: 'Emitido em {{date}}',
    tokenExpiresAt: ' · Expira em {{date}}',
    tokenRevoked: 'Revogado',
    verificationBadge: 'Verificação',
    verdict: {
      addressed: 'IA: endereçado',
      partial: 'IA: parcial',
      not_addressed: 'IA: não endereçado',
    },
  },
  dialogs: {
    decideAccept: 'Aceitar flag',
    decideReject: 'Rejeitar flag',
    decideResolve: 'Marcar como resolvida',
    decideAcceptHint: 'A nota será incluída na lista de pendências enviada ao fornecedor.',
    decideNotePlaceholder: 'Comentário opcional para registro / fornecedor',
    bulkResolveTitle: 'Resolver {{count}} flag(s) pendentes',
    bulkRejectTitle: 'Rejeitar {{count}} flag(s) pendentes',
    bulkHint: 'Esta ação não envia nada ao fornecedor — só fecha as flags pendentes.',
    bulkNotePlaceholder: 'Comentário opcional aplicado a todas',
    addFlagTitle: 'Adicionar flag de revisor',
    addFlagHint: 'Use quando você notou algo que a IA não levantou.',
    addFlagTitlePlaceholder: 'Título',
    addFlagBodyPlaceholder: 'Descrição do ponto a endereçar',
    addFlagProposedWordingPlaceholder:
      'Redação sugerida (opcional). Deixe em branco se quiser apenas apontar a flag.',
    resetReasonPrompt: 'Motivo (opcional):',
    resetReasonLabel: 'Motivo (opcional)',
    resetConfirm: 'Reverter para revisão? Status atual será preservado nos metadados de auditoria.',
    retryAiConfirm: 'Reagendar IA? As flags atuais da versão ativa serão apagadas.',
    signerHint: 'Defina quem assinará o contrato. Nome e e-mail são obrigatórios.',
    signerNamePrompt: 'Nome do signatário:',
    signerNameLabel: 'Nome do signatário',
    signerEmailPrompt: 'E-mail do signatário:',
    signerEmailLabel: 'E-mail do signatário',
    signerRolePrompt: 'Cargo do signatário (opcional):',
    signerRoleLabel: 'Cargo (opcional)',
    signerDocumentPrompt: 'Documento do signatário (opcional):',
    signerDocumentLabel: 'Documento (opcional)',
    riskApprovalNotePrompt: 'Nota para a aprovação de risco (opcional):',
    riskRejectNotePrompt: 'Motivo da rejeição do risco (opcional):',
    requestApprovalTitle: 'Solicitar aprovação',
    requestApprovalHint:
      'Escolha a etapa. O aprovador só pode ficar em branco quando o contrato tem responsável, dono de centro de custo ou responsável de departamento para roteamento automático.',
    approvalStageLabel: 'Etapa',
    approverUserIdLabel: 'ID do aprovador',
    approverUserIdPlaceholder: 'UUID do usuário aprovador',
    approverUserIdAutoRouteHint:
      'Pode ficar em branco: esta solicitação tem contexto para tentar roteamento automático.',
    approverUserIdRequiredHint:
      'Informe um aprovador, ou preencha responsável/departamento/centro de custo no contrato.',
    approvalDecisionHint: 'Registrar decisão para {{stage}}.',
  },
  toasts: {
    decisionRecorded: 'Decisão registrada.',
    decisionFailed: 'Falha ao decidir flag.',
    bulkDone_one: '{{count}} flag atualizada.',
    bulkDone_other: '{{count}} flags atualizadas.',
    bulkFailed: 'Falha em decisão em massa.',
    flagAdded: 'Flag adicionada.',
    flagAddFailed: 'Falha ao adicionar flag.',
    flagUpdated: 'Flag atualizada.',
    flagUpdateFailed: 'Falha ao atualizar flag.',
    aiQueued: 'IA reagendada — novo run em segundo plano.',
    aiQueueFailed: 'Falha ao reagendar IA.',
    resetDone: 'Revisão revertida para triagem.',
    resetFailed: 'Falha ao reverter para triagem.',
    sendBackDone: 'Enviado para o fornecedor revisar.',
    sendBackFailed: 'Não foi possível enviar para o fornecedor.',
    redlinePlanFailed: 'Não foi possível planejar o redline.',
    draftSaved: 'Rascunho salvo.',
    draftSaveFailed: 'Falha ao salvar o rascunho.',
    draftPreviewFailed: 'Falha ao gerar a prévia do rascunho.',
    lockAcquired: 'Você está editando este documento.',
    lockLost: 'Você perdeu o bloqueio de edição.',
    lockConflict: '{{name}} está editando este documento.',
    lockReleaseFailed: 'Falha ao liberar o bloqueio de edição.',
    finalizeDone: 'Aprovado e enviado para assinatura.',
    finalizeFailed: 'Não foi possível aprovar.',
    signedDone: 'Marcado como assinado.',
    signedFailed: 'Falha ao marcar como assinado.',
    previewFailed: 'Falha ao gerar preview.',
    previewPopupHint:
      'Gerando PDF. Se a prévia não abrir automaticamente, confirme que pop-ups estão habilitados.',
    downloadFailed: 'Falha ao gerar URL de download.',
    aiCompleted: 'Análise do agente concluída.',
    aiFailed: 'Falha ao rodar análise da IA.',
    engageDone: 'Revisão engajada — IA rodará em segundo plano.',
    engageFailed: 'Falha ao engajar revisão.',
    tokenIssued: 'Token emitido. Copie agora (só será mostrado uma vez).',
    tokenIssueFailed: 'Falha ao emitir token.',
    tokenRevoked: 'Token revogado.',
    tokenRevokeFailed: 'Falha ao revogar token.',
    replyAdded: 'Comentário adicionado.',
    replyAddFailed: 'Falha ao adicionar comentário.',
    signerSaved: 'Signatário salvo.',
    signerSaveFailed: 'Falha ao salvar signatário.',
    riskApprovalRequested: 'Aprovação de risco solicitada.',
    riskApprovalRequestFailed: 'Falha ao solicitar aprovação de risco.',
    riskApprovalDecided: 'Decisão de risco registrada.',
    riskApprovalDecisionFailed: 'Falha ao registrar decisão de risco.',
  },
  list: {
    columnReference: 'Referência',
    columnTitle: 'Título',
    columnStatus: 'Status',
    columnCounterparty: 'Contraparte',
    columnSubmittedAt: 'Recebido em',
    filterStatus: 'Status',
    filterAll: 'Todos',
    filterAssignedToMe: 'Atribuídos a mim',
    filtersTitle: 'Filtros',
    clearFilters: 'Limpar filtros',
    moreActions: 'Mais ações',
    resultCount: '{{count}} resultado',
    resultCount_other: '{{count}} resultados',
    empty: 'Nenhuma revisão de contrato.',
    newRequest: 'Nova revisão',
    moduleDisabledTitle: 'Revisão de contrato desativada para esta organização',
    moduleDisabledHint:
      'Ative o módulo nas configurações para criar solicitações, listar revisões e usar o fluxo em Contas a pagar.',
    openSettings: 'Abrir configurações',
  },
  intake: {
    formTitle: 'Enviar contrato para revisão',
    formSubtitle:
      'Envie o contrato e informe um e-mail de contato. Nossos agentes de IA farão uma primeira análise e nossa equipe acompanhará o caso. Você receberá um link para acompanhar o andamento e enviar revisões.',
    cardTitle: 'Detalhes do contrato',
    sectionDocument: 'Documento do contrato',
    sectionContact: 'Contato e validação',
    sectionDetails: 'Detalhes do contrato (opcional)',
    fileLabel: 'Arquivo do contrato (DOCX ou PDF, máx. 25 MB)',
    fileHint:
      'DOCX é o formato preferido — permite redline com controle de alterações do Word. PDF também é aceito.',
    dropTitle: 'Arraste o contrato aqui',
    dropHint: 'ou clique para selecionar — DOCX ou PDF, até 25 MB',
    dropActive: 'Solte o arquivo para anexar',
    selectFileButton: 'Selecionar arquivo',
    removeFile: 'Remover arquivo',
    unsupportedType: 'Formato não suportado. Envie um arquivo DOCX ou PDF.',
    draftRestored: 'Recuperamos os dados que você havia começado a preencher.',
    resendValidationCode: 'Reenviar código',
    fileBadge: '{{name}} - {{kb}} KB',
    contactEmailRequiredLabel: 'E-mail de contato *',
    contactEmailLabel: 'E-mail de contato',
    contactEmailPlaceholder: 'legal@suaempresa.com',
    contactNameLabel: 'Seu nome (opcional)',
    contactNamePlaceholder: 'Maria Silva',
    counterpartyLabel: 'Nome da contraparte (opcional)',
    counterpartyPlaceholder: 'O nome legal da empresa, se desejar adiantar',
    counterpartyHint:
      'Nossa IA extrai o nome do próprio contrato; o campo é apenas um adiantamento.',
    messageLabel: 'Mensagem (opcional)',
    messagePlaceholder: 'Algo específico que nossa equipe deve saber?',
    contractValueLabel: 'Valor do contrato (opcional)',
    contractValuePlaceholder: 'Ex.: R$ 120.000/ano',
    contractDurationLabel: 'Prazo / vigência (opcional)',
    contractDurationPlaceholder: 'Ex.: 12 meses',
    businessOwnerLabel: 'Responsável da área (opcional)',
    businessOwnerPlaceholder: 'Nome, e-mail ou área solicitante',
    purposeLabel: 'Objetivo do contrato (opcional)',
    purposePlaceholder: 'Ex.: contratação de SaaS para atendimento',
    consent: 'Ao enviar você concorda que o contrato seja processado pela IA para fins de análise.',
    submit: 'Enviar contrato',
    submitting: 'Enviando...',
    invalidUrl: 'URL de envio inválida.',
    unavailableTitle: 'Envio indisponível',
    unavailableBody:
      'Este link de envio não está válido no momento. Ele pode ter sido desativado ou estar incorreto.',
    unavailableHelp: 'Se você recebeu esta URL de um parceiro, peça que ele verifique o link.',
    successTitle: 'Recebemos sua solicitação',
    successDuplicate:
      'Já tínhamos este contrato em arquivo — abrindo o link de acompanhamento existente.',
    successPrimary: 'Obrigado! Seu contrato foi recebido.',
    successHint: 'Use o link abaixo para acompanhar o status.',
    trackingUrlLabel: 'Link de acompanhamento',
    copyTrackingUrl: 'Copiar link',
    fileTooBig: 'Arquivo excede 25 MB.',
    selectFile: 'Selecione um arquivo de contrato antes de enviar.',
    submitError: 'Não foi possível enviar o contrato. Tente novamente.',
    fillEverything: 'Preencha o e-mail e selecione um arquivo.',
    websiteHoneypotLabel: 'Website (não preencher)',
    emailValidationTitle: 'Valide seu e-mail',
    emailValidationHint:
      'Antes de enviar o contrato, vamos mandar um código para confirmar que este e-mail é seu.',
    emailValidationSentHint: 'Enviamos um código para {{email}}.',
    emailValidationDone: 'E-mail validado. Você já pode enviar o contrato.',
    requestValidationCode: 'Enviar código',
    validationCodePlaceholder: 'Código de 6 dígitos',
    verifyValidationCode: 'Validar código',
    validationCodeSent: 'Código enviado por e-mail.',
    validationCodeFailed: 'Falha ao enviar o código.',
    validationDone: 'E-mail validado.',
    validationFailed: 'Código inválido ou expirado. Peça um novo código e tente novamente.',
  },
  external: {
    tokenLabel: 'Token de acesso',
    tokenPlaceholder: 'Cole aqui o token recebido',
    loadStatus: 'Carregar status',
    requestValidationCode: 'Enviar código',
    emailValidationTitle: 'Confirme seu e-mail para acessar',
    emailValidationHint:
      'Enviaremos um código para o e-mail associado a este link antes de mostrar os detalhes.',
    emailValidationSentHint: 'Enviamos um código para {{email}}. Digite o código abaixo.',
    validationCodePlaceholder: 'Código de 6 dígitos',
    verifyValidationCode: 'Validar código',
    validationCodeSent: 'Código enviado por e-mail.',
    validationCodeFailed: 'Falha ao enviar o código.',
    validationDone: 'E-mail validado.',
    validationFailed: 'Código inválido ou expirado.',
    loadFailed: 'Não foi possível carregar o status. Verifique o token recebido.',
    contactEmail: 'E-mail',
    counterparty: 'Contraparte',
    itemsToAddress: 'Pontos a endereçar ({{count}})',
    itemsHint:
      'Nossa equipe pediu que estes pontos sejam revisados antes de prosseguir. Faça os ajustes na nova versão do documento e envie pelo formulário abaixo.',
    reviewerNotePrefix: 'Comentário do revisor: ',
    versionsTitle: 'Versões',
    versionsEmpty: 'Nenhuma versão disponível.',
    versionActive: 'atual',
    uploadTitle: 'Enviar nova versão',
    uploadHint: 'Faça upload da nova versão do contrato em DOCX ou PDF (máx. 25 MB).',
    dropTitle: 'Arraste a nova versão aqui',
    dropHint: 'ou clique para selecionar — DOCX ou PDF, até 25 MB',
    dropActive: 'Solte o arquivo para anexar',
    selectFileButton: 'Selecionar arquivo',
    removeFile: 'Remover arquivo',
    unsupportedType: 'Formato não suportado. Envie um arquivo DOCX ou PDF.',
    uploadSubmit: 'Enviar nova versão',
    uploading: 'Enviando...',
    uploadFailed: 'Falha ao enviar arquivo.',
    uploadDone: 'Nova versão enviada. Vamos te avisar assim que houver atualização.',
    historyTitle: 'Histórico',
    historyEmpty: 'Sem eventos ainda.',
    fileBadge: '{{name}} - {{kb}} KB',
    downloadFailed: 'Falha ao baixar arquivo. O token pode ter expirado.',
    downloadUnavailable: 'URL de download indisponível.',
    justSubmittedTitle: 'Solicitação recebida',
    justSubmittedDuplicateTitle: 'Já tínhamos este contrato',
    justSubmittedHint:
      'O link desta página é a sua referência permanente. Salve-o agora — qualquer pessoa com este link pode ver o status, então trate-o como uma senha.',
    justSubmittedDuplicateHint:
      'Identificamos que este arquivo já foi enviado antes. Você está vendo a revisão existente.',
    copyPageUrl: 'Copiar link desta página',
    pageUrlCopied: 'Link copiado.',
    dismissBanner: 'Fechar',
    replySent: 'Comentário enviado.',
    replyFailed: 'Falha ao enviar comentário.',
    you: 'Você',
    internalTeam: 'Time interno',
    replyPlaceholder: 'Justifique sua resposta para este ponto',
    sendReply: 'Enviar justificativa',
  },
  create: {
    title: 'Nova revisão de contrato',
    subtitle: 'Envie manualmente um contrato recebido para iniciar a revisão interna.',
    referenceLabel: 'Referência (sua identificação interna)',
    referencePlaceholder: 'CR-2026-001 ou contrato-fornecedor-x',
    titleLabel: 'Título',
    titlePlaceholder: 'MSA com Vendor X',
    counterpartyLabel: 'Nome da contraparte',
    counterpartyPlaceholder: 'Nome legal do fornecedor / prospect',
    counterpartyTypeLabel: 'Tipo da contraparte',
    counterpartyTypeVendor: 'Fornecedor',
    counterpartyTypeProspect: 'Prospect',
    counterpartyEmailLabel: 'E-mail de contato (opcional)',
    counterpartyEmailPlaceholder: 'legal@vendor.com',
    descriptionLabel: 'Notas internas (opcional)',
    descriptionPlaceholder: 'Contexto, objetivos, etc.',
    fileLabel: 'Arquivo do contrato (DOCX ou PDF, máx. 25 MB)',
    fileHint: 'DOCX é o formato preferido — permite redline com controle de alterações do Word.',
    sectionCounterparty: 'Contraparte e detalhes do contrato',
    sectionClassification: 'Classificação e responsável',
    sectionNotes: 'Notas internas',
    counterpartyRequired: 'Informe a contraparte antes de criar a revisão.',
    entityLabel: 'Entidade',
    entityPlaceholder: 'Selecione a entidade',
    departmentIdLabel: 'Departamento',
    costCenterIdLabel: 'Centro de custo',
    responsibleUserIdLabel: 'Responsável pelo contrato',
    selectPlaceholder: 'Selecione…',
    responsibleUserHint: 'Opcional — usuário interno responsável pelo contrato.',
    submit: 'Criar revisão',
    submitting: 'Criando…',
    success: 'Revisão criada.',
    failed: 'Falha ao criar revisão.',
  },
  settings: {
    title: 'Configurações de revisão de contrato',
    subtitle: 'Edite os prompts da IA e veja a URL pública para vendors.',
    publicIntakeTitle: 'URL pública de intake',
    publicIntakeNotConfigured: 'Tenant ainda não tem um publicSlug configurado.',
    publicIntakeHint:
      'Compartilhe esta URL com fornecedores para que eles enviem contratos sem precisar ser usuários do ERP.',
    publicIntakeOnLabel: 'Intake público ativado',
    publicIntakeOffLabel: 'Intake público desativado',
    publicIntakeToggleHint:
      'Quando desativado, usuários internos ainda podem revisar contratos, mas o link público retorna 404.',
    publicIntakeDisabled: 'Intake público desativado para este tenant.',
    publicIntakeSaved: 'Preferência do intake público atualizada.',
    publicIntakeSaveFailed: 'Falha ao atualizar o intake público.',
    promptsTitle: 'Prompts dos agentes de IA',
    promptsHint: 'Edite com cuidado — alterações afetam todos os contratos analisados depois.',
    legalPrompt: 'Prompt do agente Legal',
    businessPrompt: 'Prompt do agente Risco',
    promptVersion: 'Versão {{version}}',
    promptSaved: 'Prompts salvos.',
    promptSaveFailed: 'Falha ao salvar prompts.',
    contractTypesTitle: 'Tipos de contrato (registro)',
    contractTypesHint:
      'Códigos usados para encaminhar agentes por tipo. Sugestões aparecem nos campos de routing; use * nos agentes para todos os tipos.',
    contractTypeCode: 'Código',
    contractTypeLabel: 'Rótulo',
    contractTypeDescription: 'Descrição',
    addContractType: 'Adicionar tipo',
    removeContractType: 'Remover tipo de contrato',
    saveContractTypes: 'Salvar tipos',
    contractTypesSaved: 'Tipos de contrato salvos.',
    contractTypesSaveFailed: 'Falha ao salvar tipos de contrato.',
    agentCatalogSaved: 'Catálogo de agentes atualizado.',
    agentCatalogSaveFailed: 'Falha ao salvar catálogo de agentes.',
    globalFlagsSaved: 'Flags globais atualizadas.',
    globalFlagsSaveFailed: 'Falha ao salvar flags globais.',
    moduleTitle: 'Revisão de contratos',
    moduleHint:
      'Controle o acesso ao fluxo interno de revisão de contratos. O intake público é configurado separadamente abaixo.',
    moduleOnLabel: 'Ativado',
    moduleOffLabel: 'Desativado',
    moduleSaved: 'Preferência do módulo atualizada.',
    moduleSaveFailed: 'Falha ao atualizar o módulo.',
    goToRequests: 'Ir para solicitações',
    publicIntakeDisabledWhileModuleOff:
      'O intake público fica indisponível enquanto o módulo estiver desativado.',
    tabs: {
      general: 'Geral',
      contractTypes: 'Tipos de contrato',
      agents: 'Agentes',
      companyContext: 'Contexto da empresa',
    },
    companyContextTitle: 'Contexto da empresa (flags globais)',
    companyContextHint:
      'Regras curadas da empresa injetadas nos prompts dos agentes por tipo de contrato.',
    autoProcessTitle: 'Auto processar',
    autoProcessHint:
      'Após o resumo inicial (Fase A), inicia automaticamente a análise completa de flags sem exigir o botão Engajar.',
    autoProcessOnLabel: 'Análise completa automática após o resumo inicial',
    autoProcessOffLabel: 'Exigir engajamento manual antes da análise completa',
    autoProcessSaved: 'Preferência de auto processamento salva',
    autoProcessSaveFailed: 'Não foi possível salvar a preferência de auto processamento',
    companyContextFieldTitle: 'Título',
    companyContextFieldDetection: 'Como identificar',
    companyContextFieldSuggestedWording: 'Redação sugerida',
    companyContextFieldNegotiationPolicy: 'Política de negociação',
    companyContextFieldCategory: 'Categoria',
    companyContextFieldSeverity: 'Severidade',
    companyContextFieldContractTypes: 'Tipos de contrato',
    companyContextFieldState: 'Estado',
    companyContextFieldBody: 'Conteúdo',
    companyContextAllContractTypes: 'Todos',
    companyContextCategory: {
      LEGAL: 'Jurídico',
      BUSINESS: 'Negócio',
      COMPLIANCE: 'Compliance',
      VENDOR_VALIDATION: 'Validação de fornecedor',
      OTHER: 'Outro',
    },
    companyContextSeverity: {
      LOW: 'Baixa',
      MEDIUM: 'Média',
      HIGH: 'Alta',
      CRITICAL: 'Crítica',
    },
    companyContextActive: 'Ativo',
    companyContextInactive: 'Inativo',
    companyContextAdd: 'Adicionar flag',
    companyContextSave: 'Salvar contexto',
    companyContextValidation: 'Adicione ao menos uma flag válida antes de salvar.',
    retroFeedCurationTitle: 'Sugestões de revisões concluídas',
    retroFeedCurationHint:
      'Flags sugeridas automaticamente após contratos assinados. Ative para usar nas análises ou descarte.',
    retroFeedCurationApprove: 'Ativar',
    retroFeedCurationDismiss: 'Descartar',
    retroFeedMarkersTitle: 'Alertas de retro-feed',
    retroFeedMarkersHint:
      'Guidelines existentes marcadas como possível duplicata ou contradição com flags aceitas.',
    retroFeedMarkerClear: 'Limpar alerta',
    retroFeedReviewState: {
      IN_REVIEW: 'Aguardando curadoria',
      DUPLICATE: 'Possível duplicata',
      CONTRADICTING: 'Possível contradição',
    },
    agents: {
      title: 'Agentes de revisão',
      subtitle:
        'Configure os agentes que analisam contratos. Agentes combináveis do mesmo papel rodam juntos; standalone rodam em paralelo.',
      addLegalTemplate: 'Modelo Legal',
      addRiskTemplate: 'Modelo Risco',
      addAgent: 'Agente personalizado',
      addAgentTitle: 'Novo agente',
      editAgentTitle: 'Editar agente',
      editAgent: 'Editar',
      deleteAgent: 'Excluir',
      customSheetHint:
        'Agentes combináveis do mesmo papel entram na mesma chamada de IA; standalone rodam sozinhos.',
      columnName: 'Nome',
      columnRole: 'Tipo',
      columnType: 'Tipo',
      columnExecution: 'Execução',
      columnContractTypes: 'Tipos de contrato',
      columnStatus: 'Status',
      tableEmpty: 'Nenhum agente configurado. Use os modelos acima para começar.',
      allContractTypes: 'Todos os tipos',
      statusActive: 'Ativo',
      statusDisabled: 'Desativado',
      statusAlwaysOn: 'Sempre ativo',
      systemBadge: 'Sistema',
      roleLegal: 'Legal',
      roleBusiness: 'Risco comercial',
      roleVendor: 'Validação de fornecedor',
      executionMergeable: 'Combinável',
      executionStandalone: 'Standalone',
      executionHint:
        'Combinável: mesma chamada de IA com outros agentes do mesmo papel. Standalone: chamada dedicada em paralelo.',
      vendorDescription:
        'Valida identidade do fornecedor (CNPJ e razão social) em todo contrato analisado.',
      vendorLocked: 'Gerenciado pelo sistema — não editável.',
      missingLanesWarning:
        'Recomendamos pelo menos um agente Legal e um de Risco comercial ativos. A validação de fornecedor do sistema continua sempre ligada.',
      fieldName: 'Nome',
      fieldNamePlaceholder: 'Ex.: Legal SaaS',
      fieldRole: 'Tipo de revisão',
      fieldType: 'Tipo de revisão',
      fieldExecutionMode: 'Modo de execução',
      fieldContractTypes: 'Executar para',
      fieldDescription: 'Descrição (opcional)',
      fieldPrompt: 'Prompt',
      fieldModel: 'Modelo',
      fieldComplexity: 'Complexidade',
      complexityStandard: 'Padrão (rápida)',
      complexityAdvanced: 'Avançada (profunda)',
      vendorAlwaysOnNote:
        'A validação de fornecedor (CNPJ e razão social) roda automaticamente em toda análise.',
      required: 'Obrigatório',
      fieldEnabled: 'Estado',
      validationName: 'Informe um nome para o agente.',
      validationPrompt: 'O prompt deve ter pelo menos 20 caracteres.',
    },
  },
  audit: {
    requestCreated: 'Solicitação criada',
    requestCreatedBy: 'Por {{user}}',
    metadataCancel: 'Solicitação cancelada',
    metadataFinalize: 'Aprovado para assinatura',
    metadataSign: 'Marcado como assinado',
    metadataAdminReset: 'Admin: revertido para revisão',
    metadataAiRetry: 'IA reagendada',
    metadataVendorAutoCreate: 'Fornecedor (auto-criado/vinculado)',
    metadataPublicIntake: 'Solicitação criada via formulário público',
    flagDecided: 'Flag {{decision}} — {{title}}',
    versionUploaded: 'Versão v{{version}} carregada ({{source}})',
    detailReason: 'Motivo: {{reason}}',
    detailPreviousStatus: 'Status anterior: {{status}}',
    detailExternalSigningRef: 'Ref: {{ref}}',
  },
};

export const contractReviewEn: Record<string, unknown> = {
  page: {
    listTitle: 'Contract reviews',
    listSubtitle: 'Track contracts under review by your team and the AI.',
    detailBackToList: 'List',
    intakeTitle: 'Submit a contract for review',
    intakeSubtitle:
      'The document goes through an automatic initial scan and our team gets in touch.',
    intakeBranding: 'Receiving submission for',
    trackTitle: 'Contract review tracking',
    settingsTitle: 'Contract review settings',
    newRequestTitle: 'New contract review',
  },
  common: {
    loading: 'Loading...',
    loadingRequest: 'Loading request...',
    cancel: 'Cancel',
    confirm: 'Confirm',
    save: 'Save',
    copy: 'Copy',
    submit: 'Submit',
    download: 'Download',
    open: 'Open',
    close: 'Close',
    optional: 'optional',
    required: 'required',
    yes: 'Yes',
    no: 'No',
    notProvided: 'Not provided',
    none: 'None',
    by: 'By',
    at: 'At',
  },
  status: {
    internal: {
      DRAFT: 'Draft',
      PENDING_INTAKE_ACK: 'Awaiting triage',
      AI_PROCESSING: 'AI analyzing',
      READY_FOR_REVIEW: 'Ready for human review',
      IN_REVIEWER_TRIAGE: 'In reviewer triage',
      AWAITING_VENDOR: 'Awaiting vendor',
      AWAITING_SIGNING: 'Ready for signing',
      COMPLETED: 'Completed (signed)',
      CANCELLED: 'Cancelled',
      FAILED: 'Failed',
    },
    external: {
      DRAFT: 'Draft',
      PENDING_INTAKE_ACK: 'Under review',
      AI_PROCESSING: 'Under review',
      READY_FOR_REVIEW: 'Under review',
      IN_REVIEWER_TRIAGE: 'Under review',
      AWAITING_VENDOR: 'Action required — please send a new version',
      AWAITING_SIGNING: 'Approved — preparing signing',
      COMPLETED: 'Completed — signed',
      CANCELLED: 'Cancelled',
      FAILED: 'Technical issue — we will reach out',
    },
  },
  severity: {
    LOW: 'Low',
    MEDIUM: 'Medium',
    HIGH: 'High',
    CRITICAL: 'Critical',
  },
  decision: {
    PENDING: 'Pending',
    ACCEPTED: 'Accepted',
    REJECTED: 'Rejected',
    RESOLVED: 'Resolved',
  },
  category: {
    LEGAL: 'Legal',
    BUSINESS: 'Business',
    COMPLIANCE: 'Compliance',
    VENDOR_VALIDATION: 'Vendor validation',
    OTHER: 'Other',
  },
  source: {
    AGENT_LEGAL: 'Legal Agent',
    AGENT_BUSINESS_RISK: 'Risk Agent',
    AGENT_VENDOR_VALIDATION: 'Validation Agent',
    REVIEWER: 'Reviewer',
  },
  versionSource: {
    INITIAL_INTAKE: 'Initial version',
    EXTERNAL_RESUBMISSION: 'Vendor resubmission',
    INTERNAL_REVIEW: 'Internal version',
    AI_GENERATED: 'Reviewed (changes requested)',
  },
  tokenScope: {
    STATUS_VIEW: {
      label: 'Status access',
      description: 'Allows the external party to open the portal and track review status.',
    },
    DOWNLOAD_VERSION: {
      label: 'Download documents',
      description:
        'Allows the external party to open the portal and generate version download links.',
    },
    UPLOAD_VERSION: {
      label: 'Upload revised version',
      description:
        'Allows the external party to open the portal, reply on flags, and upload a revised PDF.',
    },
  },
  redline: {
    title: 'Review changes (redline)',
    hint: 'Review and adjust each change before generating the Word tracked-changes version.',
    loading: 'Planning the changes with AI…',
    empty: 'No change could be anchored in the document. You can still send without a redline.',
    anchor: 'Original text',
    newText: 'New wording',
    comment: 'Comment to the counterparty',
    generate: 'Generate redline & send',
    skipped: '{{count}} change(s) could not be applied and were skipped.',
    changeType: {
      replace: 'Replace',
      insert_after: 'Insert after',
      delete: 'Delete',
    },
  },
  buttons: {
    engageReview: 'Engage review',
    engageReviewWaiting: 'Waiting for initial scan…',
    forceAiRerun: 'Force re-run',
    sendBack: 'Send back to vendor',
    sendBackWithCount: 'Send back to vendor ({{count}})',
    sendingBack: 'Sending…',
    previewSendBack: 'Preview PDF',
    reviewRedline: 'Review redline',
    previewSendBackLoading: 'Generating preview…',
    previewDraftDocx: 'Preview draft',
    previewDraftDocxLoading: 'Generating draft…',
    editDocument: 'Edit document',
    exitEditing: 'Finish editing',
    takeOverEditing: 'Take over editing',
    approveForSigning: 'Approve and send to signing',
    approving: 'Approving…',
    markSigned: 'Mark signed',
    retryAi: 'Retry AI',
    resetToTriage: 'Reopen review',
    addReviewerFlag: '+ Add flag',
    bulkResolve: 'Resolve all',
    bulkReject: 'Reject all',
    decideAccept: 'Accept',
    decideAcceptWithNote: 'Accept with comment…',
    decideAcceptTooltip: 'Accept the flag and forward it to the vendor for revision.',
    decideReject: 'Reject',
    decideRejectWithNote: 'Reject with comment…',
    decideRejectTooltip: 'Mark as a false positive. The vendor will not see this flag.',
    decideResolve: 'Resolve',
    decideResolveWithNote: 'Resolve with comment…',
    decideResolveTooltip: 'Already handled offline. Close without forwarding to the vendor.',
    promoteGlobalFlag: 'Promote to global',
    setSigner: 'Set signer',
    requestRiskApproval: 'Request risk approval',
    approveRisk: 'Approve risk',
    rejectRisk: 'Reject risk',
    runAiNow: 'Run AI now',
    runningAi: 'Running…',
    engaging: 'Engaging…',
    create: 'Create',
    creating: 'Creating…',
    edit: 'Edit',
    delete: 'Delete',
    issueToken: 'Issue access',
    revokeToken: 'Revoke',
    copyToken: 'Copy token',
  },
  approvalStages: {
    LEGAL: 'Legal',
    BUSINESS_RISK: 'Manager / risk',
    EXECUTIVE: 'Executive approval',
  },
  tabs: {
    flags: 'Flags ({{count}})',
    pendingFlags: 'Pending flags ({{count}})',
    versions: 'Versions ({{count}})',
    tokens: 'External access',
    audit: 'Audit',
  },
  banner: {
    triageDone: 'Triage complete ({{total}} flag(s) — {{accepted}} accepted).',
    triageDoneSendBack: 'Send to the vendor for revision.',
    triageDoneApprove: 'Nothing to fix — approve and send to signing.',
    processingPreIntakeTitle: 'Initial scan running',
    processingPreIntakeBody:
      'We are extracting CNPJ, counterparty, and document type. The page refreshes itself when it finishes.',
    processingFullReviewTitle: 'Full AI review running',
    processingFullReviewBody:
      'Legal, risk, and validation lanes run in parallel when enabled. The page refreshes itself when the flags are ready.',
    readyToEngageTitle: 'Initial scan complete',
    readyToEngageBody:
      'Review the summary and extracted fields below. When ready, click Engage to start the full AI review.',
    someoneEditing: '{{name}} is editing this document.',
    someoneEditingReadOnly: 'Read-only view while {{name}} is editing.',
    someoneEditingStale: "{{name}}'s editing session expired. You can take over editing.",
  },
  editor: {
    title: 'Document editing',
    hint: 'Select a span of text to replace, insert after, or delete. Each edit becomes a tracked change and a flag.',
    readOnly: 'Read-only',
    readOnlyHint: 'Wait for the other reviewer to release the document before editing.',
    selectionPlaceholder: 'Select a span in the text to edit.',
    selectedLabel: 'Selected span',
    actionReplace: 'Replace',
    actionInsertAfter: 'Insert after',
    actionDelete: 'Delete',
    newTextLabel: 'New wording',
    newTextPlaceholder: 'Type the new wording…',
    commentLabel: 'Counterparty comment (optional)',
    apply: 'Apply edit',
    cancel: 'Cancel',
    editsCount_one: '{{count}} edit',
    editsCount_other: '{{count}} edits',
    removeEdit: 'Remove edit',
    saving: 'Saving…',
    saved: 'Draft saved',
    empty: 'No edits yet.',
  },
  detail: {
    aiSummaryTitle: 'AI summary',
    aiSummaryInitialTitle: 'AI summary — initial triage',
    collapseSummary: 'Collapse',
    expandSummary: 'Expand',
    aiSummaryEmpty: 'AI has not analyzed this document yet.',
    extractedCnpj: 'Extracted CNPJ',
    extractedCounterpartyName: 'Extracted counterparty',
    documentTypeGuess: 'Document type',
    primaryReviewer: 'Primary reviewer',
    primaryReviewerNone: 'No reviewer assigned',
    counterparty: 'Counterparty',
    contactEmail: 'Contact email',
    nothingToPreview: 'No document available to preview.',
    previewLoading: 'Loading document...',
    previewUnavailable: 'Preview not supported for this file type ({{mime}}).',
    openInNewTab: 'Open file in a new tab',
    previewFormatted: 'Formatted',
    previewPlainText: 'Text',
    showDocument: 'Show document',
    noActiveVersion: 'No active version',
    documentTypeBadge: 'Type: {{type}}',
    counterpartyBadge: 'Counterparty: {{name}}',
    cnpjBadge: 'CNPJ: {{cnpj}}',
    previewWindowTitle: 'Revision PDF preview',
    previewWindowLoading: 'Generating the PDF preview…',
    versionDeprecated: 'previous version',
    versionActive: 'active',
    versionAiAnnotated: 'Reviewed (changes requested)',
    versionEmpty: 'No versions.',
    flagsEmpty: 'No flags yet.',
    flagsEmptyHint: 'Run AI analysis to generate findings.',
    pendingFlagsEmpty: 'No pending flags.',
    pendingFlagsEmptyHint: 'When you accept, resolve, or reject a flag, it leaves this list.',
    pendingCount: '{{count}} pending',
    reviewerNote: 'Reviewer note: {{note}}',
    replyPlaceholder: 'Add comment to this flag',
    replySend: 'Send comment',
    replyAttach: 'Attach file',
    replyAttachmentRemove: 'Remove attachment',
    replyAttachmentTooMany: 'Maximum {{count}} attachments per comment',
    replyAttachmentTooLarge: '"{{name}}" exceeds the {{mb}}MB size limit',
    replyAttachmentTypeNotAllowed: '"{{name}}" is not an allowed type (PDF, PNG, JPEG or DOCX)',
    replyAttachmentDownloadFailed: 'Could not open attachment',
    externalCounterparty: 'Counterparty',
    internalReviewer: 'Internal team',
    internalReviewerFallback: 'Internal reviewer',
    proposedWordingTitle: 'Suggested wording',
    flagTitleLabel: 'Title',
    internalRationaleLabel: 'Internal rationale (reviewers only)',
    vendorMessageLabel: 'Vendor message',
    vendorMessageHint: 'Sent in the PDF and external portal when the flag is accepted.',
    legacyVendorFallbackWarning:
      'This flag has no dedicated vendor message yet. The internal rationale above is sent to the vendor in the PDF/portal until you save vendor-facing copy.',
    flagSave: 'Save changes',
    flagSaveDirty: 'Save',
    signerMissing: 'Set signer before finalizing',
    riskApprovalRequired: 'Risk approval is required before finalizing',
    approvalStagesRequired: 'Required approval stages are still pending before finalizing',
    legacyRiskPending: 'Risk approval pending (legacy flow)',
    awaitingOtherApprover: 'Awaiting approval from another manager',
    auditEmpty: 'No audit events.',
    tokenIssued: 'Token shown only once',
    tokenCopied: 'Token copied.',
    tokenHelp:
      'Enter the email that will receive access. The token is shown once and should be delivered to the counterparty through the agreed channel.',
    tokenRecipientEmail: 'Recipient email',
    tokenRecipientEmailPlaceholder: 'legal@vendor.com',
    tokenIssuedAt: 'Issued at {{date}}',
    tokenExpiresAt: ' · Expires at {{date}}',
    tokenRevoked: 'Revoked',
    verificationBadge: 'Verification',
    verdict: {
      addressed: 'AI: addressed',
      partial: 'AI: partial',
      not_addressed: 'AI: not addressed',
    },
  },
  dialogs: {
    decideAccept: 'Accept flag',
    decideReject: 'Reject flag',
    decideResolve: 'Mark as resolved',
    decideAcceptHint: 'The note will be included in the items sent to the vendor.',
    decideNotePlaceholder: 'Optional note (kept on record / sent to vendor)',
    bulkResolveTitle: 'Resolve {{count}} pending flag(s)',
    bulkRejectTitle: 'Reject {{count}} pending flag(s)',
    bulkHint: 'This action does not notify the vendor — it only closes the pending flags.',
    bulkNotePlaceholder: 'Optional note applied to all',
    addFlagTitle: 'Add reviewer flag',
    addFlagHint: 'Use when you spotted something the AI missed.',
    addFlagTitlePlaceholder: 'Title',
    addFlagBodyPlaceholder: 'Describe the point that needs to be addressed',
    addFlagProposedWordingPlaceholder:
      'Suggested wording (optional). Leave blank if you only want to flag the clause.',
    resetReasonPrompt: 'Reason (optional):',
    resetReasonLabel: 'Reason (optional)',
    resetConfirm: 'Reopen review? Current status will be preserved in audit metadata.',
    retryAiConfirm: 'Retry AI? Current flags on the active version will be wiped.',
    signerHint: 'Set who will sign the contract. Name and email are required.',
    signerNamePrompt: 'Signer name:',
    signerNameLabel: 'Signer name',
    signerEmailPrompt: 'Signer email:',
    signerEmailLabel: 'Signer email',
    signerRolePrompt: 'Signer role (optional):',
    signerRoleLabel: 'Role (optional)',
    signerDocumentPrompt: 'Signer document (optional):',
    signerDocumentLabel: 'Document (optional)',
    riskApprovalNotePrompt: 'Risk-approval note (optional):',
    riskRejectNotePrompt: 'Risk rejection reason (optional):',
    requestApprovalTitle: 'Request approval',
    requestApprovalHint:
      'Choose the stage. Approver can be blank only when the contract has a responsible user, cost center owner, or department head for automatic routing.',
    approvalStageLabel: 'Stage',
    approverUserIdLabel: 'Approver user ID',
    approverUserIdPlaceholder: 'Approver user UUID',
    approverUserIdAutoRouteHint: 'Can be blank: this request has context for automatic routing.',
    approverUserIdRequiredHint:
      'Select an approver, or set a responsible user/department/cost center on the contract.',
    approvalDecisionHint: 'Record decision for {{stage}}.',
  },
  toasts: {
    decisionRecorded: 'Decision recorded.',
    decisionFailed: 'Failed to decide flag.',
    bulkDone_one: '{{count}} flag updated.',
    bulkDone_other: '{{count}} flags updated.',
    bulkFailed: 'Bulk decision failed.',
    flagAdded: 'Flag added.',
    flagAddFailed: 'Failed to add flag.',
    flagUpdated: 'Flag updated.',
    flagUpdateFailed: 'Failed to update flag.',
    aiQueued: 'AI re-queued — running in background.',
    aiQueueFailed: 'Failed to re-queue AI.',
    resetDone: 'Review reset to triage.',
    resetFailed: 'Failed to reset to triage.',
    sendBackDone: 'Sent to vendor for revision.',
    sendBackFailed: 'Could not send to vendor.',
    redlinePlanFailed: 'Could not plan the redline.',
    draftSaved: 'Draft saved.',
    draftSaveFailed: 'Failed to save the draft.',
    draftPreviewFailed: 'Failed to generate the draft preview.',
    lockAcquired: 'You are editing this document.',
    lockLost: 'You lost the editing lock.',
    lockConflict: '{{name}} is editing this document.',
    lockReleaseFailed: 'Failed to release the editing lock.',
    finalizeDone: 'Approved and sent to signing.',
    finalizeFailed: 'Could not approve.',
    signedDone: 'Marked as signed.',
    signedFailed: 'Failed to mark as signed.',
    previewFailed: 'Failed to generate preview.',
    previewPopupHint:
      'Generating PDF. If the preview does not open automatically, make sure pop-ups are enabled.',
    downloadFailed: 'Failed to generate download URL.',
    aiCompleted: 'AI analysis completed.',
    aiFailed: 'Failed to run AI analysis.',
    engageDone: 'Review engaged — AI running in background.',
    engageFailed: 'Failed to engage review.',
    tokenIssued: 'Token issued. Copy it now (shown only once).',
    tokenIssueFailed: 'Failed to issue token.',
    tokenRevoked: 'Token revoked.',
    tokenRevokeFailed: 'Failed to revoke token.',
    replyAdded: 'Comment added.',
    replyAddFailed: 'Failed to add comment.',
    signerSaved: 'Signer saved.',
    signerSaveFailed: 'Failed to save signer.',
    riskApprovalRequested: 'Risk approval requested.',
    riskApprovalRequestFailed: 'Failed to request risk approval.',
    riskApprovalDecided: 'Risk decision recorded.',
    riskApprovalDecisionFailed: 'Failed to record risk decision.',
  },
  list: {
    columnReference: 'Reference',
    columnTitle: 'Title',
    columnStatus: 'Status',
    columnCounterparty: 'Counterparty',
    columnSubmittedAt: 'Received at',
    filterStatus: 'Status',
    filterAll: 'All',
    filterAssignedToMe: 'Assigned to me',
    filtersTitle: 'Filters',
    clearFilters: 'Clear filters',
    moreActions: 'More actions',
    resultCount: '{{count}} result',
    resultCount_other: '{{count}} results',
    empty: 'No contract reviews.',
    newRequest: 'New review',
    moduleDisabledTitle: 'Contract review is turned off for this organization',
    moduleDisabledHint:
      'Turn the module on in settings to create requests, browse reviews, and use the Accounts payable workflow.',
    openSettings: 'Open settings',
  },
  intake: {
    formTitle: 'Submit a contract for review',
    formSubtitle:
      'Upload your contract and provide a contact email. Our AI agents will run a first pass and our team will follow up. You will get a tracking link to follow progress and submit revisions.',
    cardTitle: 'Contract details',
    sectionDocument: 'Contract document',
    sectionContact: 'Contact & verification',
    sectionDetails: 'Contract details (optional)',
    fileLabel: 'Contract file (DOCX or PDF, max 25 MB)',
    fileHint:
      'DOCX is the preferred format — it enables Word tracked-changes redlines. PDF is also accepted.',
    dropTitle: 'Drag the contract here',
    dropHint: 'or click to browse — DOCX or PDF, up to 25 MB',
    dropActive: 'Drop the file to attach',
    selectFileButton: 'Select file',
    removeFile: 'Remove file',
    unsupportedType: 'Unsupported format. Please upload a DOCX or PDF file.',
    draftRestored: 'We restored the details you had started filling in.',
    resendValidationCode: 'Resend code',
    fileBadge: '{{name}} - {{kb}} KB',
    contactEmailRequiredLabel: 'Contact email *',
    contactEmailLabel: 'Contact email',
    contactEmailPlaceholder: 'legal@yourcompany.com',
    contactNameLabel: 'Your name (optional)',
    contactNamePlaceholder: 'Jane Doe',
    counterpartyLabel: 'Counterparty name (optional)',
    counterpartyPlaceholder: 'The legal company name, if you want to provide it upfront',
    counterpartyHint:
      'Our AI extracts the name from the contract itself; this field is only a hint.',
    messageLabel: 'Message (optional)',
    messagePlaceholder: 'Anything specific our team should know?',
    contractValueLabel: 'Contract value (optional)',
    contractValuePlaceholder: 'E.g. BRL 120,000/year',
    contractDurationLabel: 'Term / duration (optional)',
    contractDurationPlaceholder: 'E.g. 12 months',
    businessOwnerLabel: 'Business owner (optional)',
    businessOwnerPlaceholder: 'Name, email, or requesting area',
    purposeLabel: 'Contract purpose (optional)',
    purposePlaceholder: 'E.g. SaaS subscription for support team',
    consent: 'By submitting you agree to the contract being processed by AI for analysis purposes.',
    submit: 'Submit contract',
    submitting: 'Submitting...',
    invalidUrl: 'Invalid intake URL.',
    unavailableTitle: 'Intake unavailable',
    unavailableBody:
      "This intake link isn't valid right now. It may have been deactivated or it may be wrong.",
    unavailableHelp: 'If you received this URL from a partner, please ask them to verify it.',
    successTitle: 'We received your submission',
    successDuplicate: 'We already had this contract on file — showing the existing tracking link.',
    successPrimary: 'Thanks! Your contract was received.',
    successHint: 'Use the link below to track the status.',
    trackingUrlLabel: 'Tracking link',
    copyTrackingUrl: 'Copy link',
    fileTooBig: 'File exceeds 25 MB.',
    selectFile: 'Select a contract file before submitting.',
    submitError: 'Could not submit the contract. Please try again.',
    fillEverything: 'Provide an email and select a file.',
    websiteHoneypotLabel: 'Website (do not fill)',
    emailValidationTitle: 'Validate your email',
    emailValidationHint:
      'Before submitting the contract, we will send a code to confirm this email is yours.',
    emailValidationSentHint: 'We sent a code to {{email}}.',
    emailValidationDone: 'Email verified. You can submit the contract now.',
    requestValidationCode: 'Send code',
    validationCodePlaceholder: '6-digit code',
    verifyValidationCode: 'Verify code',
    validationCodeSent: 'Code sent by email.',
    validationCodeFailed: 'Failed to send the code.',
    validationDone: 'Email verified.',
    validationFailed: 'Invalid or expired code. Request a new code and try again.',
  },
  external: {
    tokenLabel: 'Access token',
    tokenPlaceholder: 'Paste the token you received',
    loadStatus: 'Load status',
    requestValidationCode: 'Send code',
    emailValidationTitle: 'Confirm your email to access',
    emailValidationHint:
      'We will send a code to the email associated with this link before showing the details.',
    emailValidationSentHint: 'We sent a code to {{email}}. Enter it below.',
    validationCodePlaceholder: '6-digit code',
    verifyValidationCode: 'Verify code',
    validationCodeSent: 'Code sent by email.',
    validationCodeFailed: 'Failed to send the code.',
    validationDone: 'Email verified.',
    validationFailed: 'Invalid or expired code.',
    loadFailed: 'Could not load the status. Check the token you received.',
    contactEmail: 'Email',
    counterparty: 'Counterparty',
    itemsToAddress: 'Items to address ({{count}})',
    itemsHint:
      'Our team asked you to review these points before proceeding. Make the changes in the new version of the document and submit it via the form below.',
    reviewerNotePrefix: 'Reviewer note: ',
    versionsTitle: 'Versions',
    versionsEmpty: 'No versions available.',
    versionActive: 'current',
    uploadTitle: 'Submit a new version',
    uploadHint: 'Upload the new version of the contract as DOCX or PDF (max 25 MB).',
    dropTitle: 'Drag the new version here',
    dropHint: 'or click to browse — DOCX or PDF, up to 25 MB',
    dropActive: 'Drop the file to attach',
    selectFileButton: 'Select file',
    removeFile: 'Remove file',
    unsupportedType: 'Unsupported format. Please upload a DOCX or PDF file.',
    uploadSubmit: 'Submit new version',
    uploading: 'Submitting...',
    uploadFailed: 'Failed to upload file.',
    uploadDone: 'New version submitted. We will notify you when there is an update.',
    historyTitle: 'History',
    historyEmpty: 'No events yet.',
    fileBadge: '{{name}} - {{kb}} KB',
    downloadFailed: 'Failed to download. The token may have expired.',
    downloadUnavailable: 'Download URL unavailable.',
    justSubmittedTitle: 'Submission received',
    justSubmittedDuplicateTitle: 'We already had this contract',
    justSubmittedHint:
      "This page's URL is your permanent reference. Save it now — anyone with the link can view status, so treat it like a password.",
    justSubmittedDuplicateHint:
      'We detected this exact file has been submitted before. You are viewing the existing review.',
    copyPageUrl: "Copy this page's link",
    pageUrlCopied: 'Link copied.',
    dismissBanner: 'Dismiss',
    replySent: 'Comment sent.',
    replyFailed: 'Failed to send comment.',
    you: 'You',
    internalTeam: 'Internal team',
    replyPlaceholder: 'Explain your answer for this item',
    sendReply: 'Send justification',
  },
  create: {
    title: 'New contract review',
    subtitle: 'Manually upload a received contract to start internal review.',
    referenceLabel: 'Reference (your internal id)',
    referencePlaceholder: 'CR-2026-001 or vendor-x-msa',
    titleLabel: 'Title',
    titlePlaceholder: 'MSA with Vendor X',
    counterpartyLabel: 'Counterparty name',
    counterpartyPlaceholder: 'Legal name of the vendor / prospect',
    counterpartyTypeLabel: 'Counterparty type',
    counterpartyTypeVendor: 'Vendor',
    counterpartyTypeProspect: 'Prospect',
    counterpartyEmailLabel: 'Contact email (optional)',
    counterpartyEmailPlaceholder: 'legal@vendor.com',
    descriptionLabel: 'Internal notes (optional)',
    descriptionPlaceholder: 'Context, objectives, etc.',
    fileLabel: 'Contract file (DOCX or PDF, max 25 MB)',
    fileHint: 'DOCX is preferred — it enables Word tracked-changes redlining.',
    sectionCounterparty: 'Counterparty & contract details',
    sectionClassification: 'Classification & owner',
    sectionNotes: 'Internal notes',
    counterpartyRequired: 'Enter the counterparty before creating the review.',
    entityLabel: 'Entity',
    entityPlaceholder: 'Select entity',
    departmentIdLabel: 'Department',
    costCenterIdLabel: 'Cost center',
    responsibleUserIdLabel: 'Contract responsible',
    selectPlaceholder: 'Select…',
    responsibleUserHint: 'Optional — internal user responsible for the contract.',
    submit: 'Create review',
    submitting: 'Creating…',
    success: 'Review created.',
    failed: 'Failed to create review.',
  },
  settings: {
    title: 'Contract review settings',
    subtitle: 'Edit AI prompts and view the public URL for vendors.',
    publicIntakeTitle: 'Public intake URL',
    publicIntakeNotConfigured: 'Tenant has no publicSlug configured yet.',
    publicIntakeHint:
      'Share this URL with vendors so they can submit contracts without being ERP users.',
    publicIntakeOnLabel: 'Public intake enabled',
    publicIntakeOffLabel: 'Public intake disabled',
    publicIntakeToggleHint:
      'When disabled, internal users can still review contracts, but the public link returns 404.',
    publicIntakeDisabled: 'Public intake is disabled for this tenant.',
    publicIntakeSaved: 'Public intake preference updated.',
    publicIntakeSaveFailed: 'Failed to update public intake preference.',
    promptsTitle: 'AI agent prompts',
    promptsHint: 'Edit carefully — changes affect every contract analyzed afterwards.',
    legalPrompt: 'Legal agent prompt',
    businessPrompt: 'Risk agent prompt',
    promptVersion: 'Version {{version}}',
    promptSaved: 'Prompts saved.',
    promptSaveFailed: 'Failed to save prompts.',
    contractTypesTitle: 'Contract types (registry)',
    contractTypesHint:
      'Codes used to route agents by contract type. Fields below suggest known codes; use * on agents for all types.',
    contractTypeCode: 'Code',
    contractTypeLabel: 'Label',
    contractTypeDescription: 'Description',
    addContractType: 'Add type',
    removeContractType: 'Remove contract type',
    saveContractTypes: 'Save types',
    contractTypesSaved: 'Contract types saved.',
    contractTypesSaveFailed: 'Failed to save contract types.',
    agentCatalogSaved: 'Agent catalog updated.',
    agentCatalogSaveFailed: 'Failed to save agent catalog.',
    globalFlagsSaved: 'Global flags updated.',
    globalFlagsSaveFailed: 'Failed to save global flags.',
    moduleTitle: 'Contract review',
    moduleHint:
      'Control access to the internal contract review workflow. Public intake is configured separately below.',
    moduleOnLabel: 'On',
    moduleOffLabel: 'Off',
    moduleSaved: 'Module preference updated.',
    moduleSaveFailed: 'Failed to update module preference.',
    goToRequests: 'Go to requests',
    publicIntakeDisabledWhileModuleOff:
      'Public intake is unavailable while the module is turned off.',
    tabs: {
      general: 'General',
      contractTypes: 'Contract types',
      agents: 'Agents',
      companyContext: 'Company context',
    },
    companyContextTitle: 'Company context (global flags)',
    companyContextHint: 'Curated company-level rules injected into agent prompts by contract type.',
    autoProcessTitle: 'Auto-process',
    autoProcessHint:
      'After the initial summary (Phase A), automatically starts full flag analysis without requiring Engage.',
    autoProcessOnLabel: 'Automatic full analysis after initial summary',
    autoProcessOffLabel: 'Require manual engage before full analysis',
    autoProcessSaved: 'Auto-process preference saved',
    autoProcessSaveFailed: 'Failed to save auto-process preference',
    companyContextFieldTitle: 'Title',
    companyContextFieldDetection: 'How to identify',
    companyContextFieldSuggestedWording: 'Suggested wording',
    companyContextFieldNegotiationPolicy: 'Negotiation policy',
    companyContextFieldCategory: 'Category',
    companyContextFieldSeverity: 'Severity',
    companyContextFieldContractTypes: 'Contract types',
    companyContextFieldState: 'State',
    companyContextFieldBody: 'Body',
    companyContextAllContractTypes: 'All',
    companyContextCategory: {
      LEGAL: 'Legal',
      BUSINESS: 'Business',
      COMPLIANCE: 'Compliance',
      VENDOR_VALIDATION: 'Vendor validation',
      OTHER: 'Other',
    },
    companyContextSeverity: {
      LOW: 'Low',
      MEDIUM: 'Medium',
      HIGH: 'High',
      CRITICAL: 'Critical',
    },
    companyContextActive: 'Active',
    companyContextInactive: 'Inactive',
    companyContextAdd: 'Add flag',
    companyContextSave: 'Save context',
    companyContextValidation: 'Add at least one valid flag before saving.',
    retroFeedCurationTitle: 'Suggestions from completed reviews',
    retroFeedCurationHint:
      'Flags proposed automatically after contracts are signed. Activate to use in analysis or dismiss.',
    retroFeedCurationApprove: 'Activate',
    retroFeedCurationDismiss: 'Dismiss',
    retroFeedMarkersTitle: 'Retro-feed alerts',
    retroFeedMarkersHint:
      'Existing guidelines flagged as possible duplicates or contradictions with accepted review flags.',
    retroFeedMarkerClear: 'Clear alert',
    retroFeedReviewState: {
      IN_REVIEW: 'Awaiting curation',
      DUPLICATE: 'Possible duplicate',
      CONTRADICTING: 'Possible contradiction',
    },
    agents: {
      title: 'Review agents',
      subtitle:
        'Configure contract review agents. Mergeable agents in the same role run together; standalone agents run in parallel.',
      addLegalTemplate: 'Legal template',
      addRiskTemplate: 'Risk template',
      addAgent: 'Custom agent',
      addAgentTitle: 'New agent',
      editAgentTitle: 'Edit agent',
      editAgent: 'Edit',
      deleteAgent: 'Delete',
      customSheetHint:
        'Mergeable agents in the same role share one AI call; standalone agents get their own parallel call.',
      columnName: 'Name',
      columnRole: 'Type',
      columnType: 'Type',
      columnExecution: 'Execution',
      columnContractTypes: 'Contract types',
      columnStatus: 'Status',
      tableEmpty: 'No agents configured yet. Use the templates above to get started.',
      allContractTypes: 'All types',
      statusActive: 'Active',
      statusDisabled: 'Disabled',
      statusAlwaysOn: 'Always on',
      systemBadge: 'System',
      roleLegal: 'Legal',
      roleBusiness: 'Business risk',
      roleVendor: 'Vendor validation',
      executionMergeable: 'Mergeable',
      executionStandalone: 'Standalone',
      executionHint:
        'Mergeable: one AI call combined with other agents in the same role. Standalone: dedicated parallel call.',
      vendorDescription:
        'Validates counterparty identity (CNPJ and legal name) on every reviewed contract.',
      vendorLocked: 'System-managed — not editable.',
      missingLanesWarning:
        'We recommend at least one active Legal agent and one Business Risk agent. System vendor validation always runs.',
      fieldName: 'Name',
      fieldNamePlaceholder: 'e.g. SaaS Legal',
      fieldRole: 'Review type',
      fieldType: 'Review type',
      fieldExecutionMode: 'Execution mode',
      fieldContractTypes: 'Runs on',
      fieldDescription: 'Description (optional)',
      fieldPrompt: 'Prompt',
      fieldModel: 'Model',
      fieldComplexity: 'Complexity',
      complexityStandard: 'Standard (fast)',
      complexityAdvanced: 'Advanced (deep)',
      vendorAlwaysOnNote:
        'Vendor validation (CNPJ and legal name) runs automatically on every analysis.',
      required: 'Required',
      fieldEnabled: 'State',
      validationName: 'Enter a name for the agent.',
      validationPrompt: 'Prompt must be at least 20 characters.',
    },
  },
  audit: {
    requestCreated: 'Request created',
    requestCreatedBy: 'By {{user}}',
    metadataCancel: 'Request cancelled',
    metadataFinalize: 'Approved for signing',
    metadataSign: 'Marked as signed',
    metadataAdminReset: 'Admin: reset to triage',
    metadataAiRetry: 'AI re-queued',
    metadataVendorAutoCreate: 'Vendor (auto-created/linked)',
    metadataPublicIntake: 'Submitted via public form',
    flagDecided: 'Flag {{decision}} — {{title}}',
    versionUploaded: 'Version v{{version}} uploaded ({{source}})',
    detailReason: 'Reason: {{reason}}',
    detailPreviousStatus: 'Previous status: {{status}}',
    detailExternalSigningRef: 'Ref: {{ref}}',
  },
};
